import React, { useContext, useMemo } from 'react';
import { ApplicationContext, backArrowForProgressBarWithStepsMobile } from 'src/features/order';
import { WizardPageStep } from '../wizard/wizard';
import { VodaPayLogoV2 } from 'src/features/common/components/vodapay-logo/VodaPayLogoV2';
import { isDeviceSelected } from '../../helpers';
import { DEVICE_NAMES } from 'src/features/order/tech-debt/product-details-extra';
import { PageName } from 'src/features/order/pages/page-names';
import { Environment } from 'src/Environment';
import './progress-bar-with-steps.scss';

export type ProgressStepsBarProps = {
  steps: Array<WizardPageStep>;
  currentStep: WizardPageStep;
  automationIdPrefix: string;
  onBack: () => void;
  hideBack: boolean;
  hideProgressBar?: boolean;
};

export function ProgressBarWithSteps({
  steps,
  currentStep,
  automationIdPrefix,
  onBack,
  hideBack,
  hideProgressBar,
}: ProgressStepsBarProps) {
  type GroupOfSteps = { step: WizardPageStep; originalIndex: number };
  type GroupOfStepsWithProgress = { progress: number; isActive: boolean } & WizardPageStep;

  const groupByStepName = (steps: Array<WizardPageStep>): Array<Array<GroupOfSteps>> => {
    const result: Array<Array<GroupOfSteps>> = [];
    let groupedStep: Array<GroupOfSteps> = [];
    steps.forEach((step, index) => {
      if (groupedStep.length === 0 || groupedStep[0].step.group === step.group) {
        // start of new group, or adding to existing
        groupedStep.push({
          step: step,
          originalIndex: index,
        });
      } else if (groupedStep.length > 0) {
        // end of current group
        result.push(groupedStep);
        groupedStep = [
          {
            step: step,
            originalIndex: index,
          },
        ];
      }
    });

    if (groupedStep.length != 0) {
      result.push(groupedStep);
    }
    return result;
  };

  const getFlattenedStepsWithProgress = (
    groupedSteps: Array<Array<GroupOfSteps>>,
    currentStepIndex: number,
  ): Array<GroupOfStepsWithProgress> =>
    groupedSteps.map((groupedStep, groupIndex) => {
      const groupLength = groupedStep.length;
      const indexStart = groupedStep[0].originalIndex;
      const indexEnd = groupedStep[groupLength - 1].originalIndex;
      const isLastStepOfLastGroup =
        groupIndex === groupedSteps.length - 1 && currentStepIndex === indexEnd;

      if (groupedStep.length === 1) {
        // this step has no substeps
        const step = groupedStep[0].step;
        return {
          ...step,
          progress:
            isLastStepOfLastGroup || indexStart < currentStepIndex
              ? 100
              : indexStart === currentStepIndex
              ? 50
              : 0,
          isActive: !isLastStepOfLastGroup && indexStart === currentStepIndex,
        };
      } else {
        // step has substeps, progress = number of complete substeps / number of total substeps
        // but if this is the last step in the flow, and we are on it, the step is complete,
        // because the last step isn't really a step - its a "you are done" step.
        const isGroupComplete = currentStepIndex > indexEnd || isLastStepOfLastGroup;
        const isGroupNotStarted = currentStepIndex < indexStart;
        const progress = isGroupNotStarted
          ? 0
          : isGroupComplete
          ? 100
          : ((groupLength - (indexEnd - currentStepIndex + 1)) / groupLength) * 100;

        return {
          ...groupedStep[0].step,
          progress: Math.round(progress),
          isActive: !isGroupNotStarted && !isGroupComplete,
        };
      }
    });

  const getProgressBarComponents = (
    steps: Array<GroupOfStepsWithProgress>,
  ): Array<React.ReactFragment> => {
    const progressValues = steps.flatMap((step, index) => {
      const stepProgress = step.progress;
      const isStart = index === 0;
      const isEnd = index === steps.length - 1;
      const result: Array<number> = [];

      if (isStart) {
        result.push(0);
      }

      result.push(Math.min(50, stepProgress));
      result.push(Math.max(0, stepProgress - 50));

      if (isEnd) {
        result.push(0);
      }

      return result;
    });

    const progressBarComponents: Array<React.ReactFragment> = [];

    for (let index = 0; index < steps.length; index++) {
      const step = steps[index];
      const props = {
        progressLeft: progressValues[index * 2] * 2,
        progressRight: progressValues[index * 2 + 1] * 2,
        isComplete: step.progress === 100,
        isActive: step.isActive,
        isStart: index === 0,
        isEnd: index === steps.length - 1,
        stepNumber: index + 1,
      };

      progressBarComponents.push(<ProgressBarComponent {...props} />);
    }

    return progressBarComponents;
  };

  const ProgressBarComponent = ({
    progressLeft,
    progressRight,
    isComplete,
    isActive,
    isStart,
    isEnd,
    stepNumber,
  }) => {
    return (
      <>
        <div className="columns is-gapless is-vcentered">
          <div className="column">
            <div className="step-bar">
              {isStart ? (
                <div className="step-spacer" style={{ width: `calc(50}%)` }} />
              ) : (
                <>
                  <div className="step-progress" style={{ width: `calc(${progressLeft}%)` }} />
                  <div
                    className="step-spacer right-side-border-radius-2px"
                    style={{ width: `calc(${Math.max(0, 100 - progressLeft)}%)` }}
                  />
                </>
              )}
            </div>
          </div>

          <div className="column is-narrow">
            <div className={`progress-step ${isComplete ? 'completed' : isActive ? 'active' : ''}`}>
              {isComplete && <div className="icon-check-mark icon" />}
              {isActive && <div className="step-number">{stepNumber}</div>}
            </div>
          </div>

          <div className="column">
            <div className="step-bar">
              {isEnd ? (
                <div className="step-spacer" style={{ width: `calc(50}%)` }} />
              ) : (
                <>
                  <div
                    className="step-progress left-side-border-radius-2px"
                    style={{ width: `calc(${progressRight}%)` }}
                  />
                  <div
                    className="step-spacer"
                    style={{ width: `calc(${Math.max(0, 100 - progressRight)}%)` }}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </>
    );
  };

  const ficaOnWizard = {
    route: PageName.FICA,
    page: PageName.FICA,
    group: PageName.FICA,
    progress: 0,
    isActive: false,
  };
  const { catalogue } = useContext(ApplicationContext);
  const groupedSteps = groupByStepName(steps);
  const isDPO = !Environment.current.disabledFeatures.dpo;
  const currentStepIndex = steps.findIndex(
    (step) => step.route === currentStep.route && step.group === currentStep.group,
  );
  const groupedStepsWithProgress = getFlattenedStepsWithProgress(groupedSteps, currentStepIndex);
  const kwikaOrMaxSelected = useMemo(
    () => isDeviceSelected(catalogue, [DEVICE_NAMES.VODAPAY_KWIKA, DEVICE_NAMES.VODAPAY_MAX]),
    [catalogue?.products],
  );
  const KwikaAndMaxGroupedStepsWithProgress = groupedStepsWithProgress
    .map((item) =>
      item.group === PageName.PAY ? { ...item, group: PageName.CREATE_PROFILE } : item,
    )
    .concat(ficaOnWizard);

  const currentGroup = groupedSteps.findIndex((group) => {
    const groupLength = group.length;
    const indexStart = group[0].originalIndex;
    const indexEnd = group[groupLength - 1].originalIndex;
    return currentStepIndex >= indexStart && currentStepIndex <= indexEnd;
  });

  return (
    <div className={`ProgressBarWithSteps ${isDPO ? 'add-shadow' : ''}`}>
      <div className="progress-block is-hidden-mobile">
        <VodaPayLogoV2 />
        {!hideProgressBar && (
          <div className="progress-container">
            <div className="columns is-gapless is-vcentered">
              {getProgressBarComponents(
                kwikaOrMaxSelected ? KwikaAndMaxGroupedStepsWithProgress : groupedStepsWithProgress,
              ).map((progressBarComponent, index) => {
                return (
                  <div className="column" key={`step-${index}`}>
                    {progressBarComponent}
                  </div>
                );
              })}
            </div>
            <div className="columns is-gapless step-label-columns">
              {(kwikaOrMaxSelected
                ? KwikaAndMaxGroupedStepsWithProgress
                : groupedStepsWithProgress
              ).map((step, index) => (
                <div className="column " key={`step-${index}`}>
                  <div
                    className={`has-text-centered step-label ${
                      step.progress == 100 ? 'completed' : step.isActive ? 'active' : ''
                    }`}
                    automationid={`${automationIdPrefix}_Div_${
                      index === currentStepIndex ? 'Current' : 'Other'
                    }_Step_Name`}
                  >
                    {step.group}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {!hideProgressBar && (
        <div className="mobile-heading is-hidden-tablet">
          {!hideBack && (
            <span
              className="mobile-back-arrow-button"
              onClick={() => onBack()}
              automationid="Onboarding_Create_Account_Button_Back_To_Previous_Step"
            >
              <img src={backArrowForProgressBarWithStepsMobile} />
            </span>
          )}

          <div className="has-padding-4">
            <div className="current-step-heading-mobile">
              Step {currentGroup + 1} of {groupedStepsWithProgress.length}
            </div>
            {currentStepIndex <= steps.length - 1 && (
              <div
                className="step-name-heading-mobile is-4 has-margin-0"
                automationid={`${automationIdPrefix}_Div_Current_Step_Name`}
              >
                {steps[currentStepIndex].group}
              </div>
            )}
          </div>
          <div className="columns is-mobile is-gapless mobile-progress">
            {steps.map((step, index) => (
              <div className={`column`} key={`step-${index}`}>
                <div
                  className={`mobile-step ${index <= currentStepIndex ? 'completed' : ''}`}
                ></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
